import { z } from 'zod';
import { schemaDebug } from '../../../utils/schemaLogger';
import { EmvDataSchema, ReceiptDataSchema, ReceiptObjectDataSchema } from '../common/Receipt';

// User defined field schema - flexible key-value pairs (same as request)
const UserFieldSchema = z.record(z.string(), z.string());

// BIN information schema
const BinInfoSchema = z.object({
  country: z.string().optional(),
  product: z.enum(['A', 'D', 'M', 'N', 'V'], {
    message: "Product must be A (Amex), D (Discover), M (Mastercard), N (Non-co-branded debit), or V (Visa)"
  }).optional(),
  bin: z.string().optional(),
  cardusestring: z.string().optional(),
  gsa: z.boolean().optional(),
  corporate: z.boolean().optional(),
  fsa: z.boolean().optional(),
  subtype: z.string().optional(),
  purchase: z.boolean().optional(),
  prepaid: z.boolean().optional(),
  issuer: z.string().optional(),
  binlo: z.string().optional(),
  binhi: z.string().optional(),
}).strict();

// Main authorization response schema
const AuthResponseSchema = z.object({
  // Core response fields
  respstat: z.enum(['A', 'B', 'C'], {
    message: "Response status must be A (Approved), B (Retry), or C (Declined)"
  }).optional(),
  retref: z.string()
    .max(32, { message: "Retrieval reference cannot exceed 32 characters" })
    .optional(),
  account: z.string()
    .max(27, { message: "Account cannot exceed 27 characters" })
    .optional(),
  expiry: z.string()
    .length(4, { message: "Expiry must be exactly 4 characters (MMYY)" })
    .regex(/^\d{4}$/, { message: "Expiry must be 4 digits in MMYY format" })
    .optional(),
  token: z.string()
    .max(19, { message: "Token cannot exceed 19 characters" })
    .optional(),
  amount: z.string()
    .max(12, { message: "Amount cannot exceed 12 characters" })
    .optional(),
  orderId: z.string()
    .max(50, { message: "Order ID cannot exceed 50 characters" })
    .optional(),
  merchid: z.string()
    .max(16, { message: "Merchant ID cannot exceed 16 characters" })
    .optional(),
  batchid: z.string()
    .max(12, { message: "Batch ID cannot exceed 12 characters" })
    .optional(),

  // Response codes and text
  respcode: z.string()
    .max(3, { message: "Response code cannot exceed 3 characters" })
    .optional(),
  resptext: z.string()
    .max(40, { message: "Response text cannot exceed 40 characters" })
    .optional(),
  respproc: z.string()
    .max(4, { message: "Response processor cannot exceed 4 characters" })
    .optional(),
  cardproc: z.string()
    .max(4, { message: "Card processor cannot exceed 4 characters" })
    .optional(),

  // Profile information
  profileid: z.string()
    .max(20, { message: "Profile ID cannot exceed 20 characters" })
    .optional(),
  acctid: z.string()
    .max(3, { message: "Account ID cannot exceed 3 characters" })
    .optional(),

  // Card on file
  cof: z.enum(['C', 'M'], {
    message: "Card on file must be C (Customer-initiated) or M (Merchant-initiated)"
  }).optional(),

  // Verification responses
  avsresp: z.string()
    .max(2, { message: "AVS response cannot exceed 2 characters" })
    .optional(),
  cvvresp: z.enum(['M', 'N', 'P', 'S', 'U', 'X', ''], {
    message: "CVV response must be M, N, P, S, U, X, or blank"
  }).optional(),

  // BIN information
  binInfo: BinInfoSchema.optional(),
  bintype: z.string()
    .max(16, { message: "BIN type cannot exceed 16 characters" })
    .optional(),

  // Entry mode
  entrymode: z.enum([
    'Keyed', 'Moto', 'ECommerce', 'Recurring', 'Swipe (Non EMV)',
    'DigitalWallet', 'Digital Wallet',  // Accept both formats
    'EMVContact', 'Contactless', 'Fallback to Track',
    'Fallback to Keyed'
  ], {
    message: "Entry mode must be a valid entry mode value"
  }).transform((value) => {
    // Normalize "Digital Wallet" to canonical "DigitalWallet"
    return value === 'Digital Wallet' ? 'DigitalWallet' : value;
  }).optional(),

  // Fee information
  fee_amount: z.string()
    .max(14, { message: "Fee amount cannot exceed 14 characters" })
    .optional(),
  fee_authcode: z.string()
    .max(6, { message: "Fee auth code cannot exceed 6 characters" })
    .optional(),
  fee_format: z.enum(['flat', 'percent'], {
    message: "Fee format must be flat or percent"
  }).optional(),
  fee_merchid: z.string()
    .max(16, { message: "Fee merchant ID cannot exceed 16 characters" })
    .optional(),
  fee_retref: z.string().optional(),
  fee_type: z.enum([
    'CVNFEE', 'SURCHRG', 'SRVCFEE', 'SRVC_WAIVED',
    'CVN_WAIVED', 'SURCHRG_WAIVED', 'FEE_TXN'
  ], {
    message: "Fee type must be a valid fee type code"
  }).optional(),
  fee_value: z.string().optional(),

  // Decline information
  declineCategory: z.enum(['1', '2', '3', '4'], {
    message: "Decline category must be 1, 2, 3, or 4"
  }).optional(),
  declineCategoryText: z.string().optional(),
  merchAdviceCode: z.string()
    .max(2, { message: "Merchant advice code cannot exceed 2 characters" })
    .optional(),
  merchAdviceText: z.string().optional(),
  assocRespCode: z.string()
    .max(3, { message: "Association response code cannot exceed 3 characters" })
    .optional(),
  assocRespText: z.string().optional(),

  // Authorization and signature
  authcode: z.union([
    z.string().max(6, { message: "Auth code cannot exceed 6 characters" }),
    z.number().int().positive()
  ])
    .optional(),
  signature: z.string()
    .max(6144, { message: "Signature data cannot exceed 6144 characters" })
    .optional(),

  // Card type information
  commcard: z.enum(['Y', 'N'], {
    message: "Commercial card flag must be Y or N"
  }).optional(),

  // EMV data
  emv: z.string().optional(),
  emvTagData: z.union([
    z.string().transform((str): z.infer<typeof EmvDataSchema> | undefined => {
      // The wire may carry emvTagData as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = EmvDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof EmvDataSchema>;
      } catch {
        schemaDebug('Failed to parse emvTagData JSON, returning undefined');
        return undefined;
      }
    }),
    EmvDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),

  // Receipt data (can be JSON string from API or already-parsed object from DB)
  receipt: z.union([
    z.string().transform((str): z.infer<typeof ReceiptDataSchema> | undefined => {
      // The wire may carry receipt as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = ReceiptDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptDataSchema>;
      } catch {
        schemaDebug('Failed to parse receipt JSON, returning undefined');
        return undefined;
      }
    }),
    ReceiptDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),
  receiptObj: z.any().transform((value): z.infer<typeof ReceiptObjectDataSchema> | undefined => {
    // Handle undefined/null
    if (value === undefined || value === null) {
      return undefined;
    }

    // If it's already an object, validate and return it
    if (typeof value === 'object') {
      const validated = ReceiptObjectDataSchema.safeParse(value);
      return validated.success ? validated.data : value as z.infer<typeof ReceiptObjectDataSchema>;
    }

    // If it's a string, parse it as JSON
    if (typeof value === 'string') {
      if (value.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(value);
        const validated = ReceiptObjectDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptObjectDataSchema>;
      } catch {
        schemaDebug('Failed to parse receiptObj JSON, returning undefined');
        return undefined;
      }
    }

    // Otherwise return undefined
    return undefined;
  }).optional(),

  // User defined fields
  userfields: z.array(UserFieldSchema).optional(),
}).strict();

// Schema for approved responses
const ApprovedResponseSchema = AuthResponseSchema.safeExtend({
  respstat: z.literal('A'),
  retref: z.string()
    .min(1, { message: "Retrieval reference is required for approved transactions" })
    .max(32, { message: "Retrieval reference cannot exceed 32 characters" }),
});

// Schema for declined responses
const DeclinedResponseSchema = AuthResponseSchema.safeExtend({
  respstat: z.literal('C'),
  respcode: z.string()
    .min(1, { message: "Response code is required for declined transactions" })
    .max(3, { message: "Response code cannot exceed 3 characters" }),
  resptext: z.string()
    .min(1, { message: "Response text is required for declined transactions" })
    .max(40, { message: "Response text cannot exceed 40 characters" }),
});

// Schema for retry responses
const RetryResponseSchema = AuthResponseSchema.safeExtend({
  respstat: z.literal('B'),
});

// Export type definitions
export type UserFieldType = z.infer<typeof UserFieldSchema>;
export type BinInfoType = z.infer<typeof BinInfoSchema>;
export type AuthResponseType = z.infer<typeof AuthResponseSchema>;
export type ApprovedResponseType = z.infer<typeof ApprovedResponseSchema>;
export type DeclinedResponseType = z.infer<typeof DeclinedResponseSchema>;
export type RetryResponseType = z.infer<typeof RetryResponseSchema>;

export default AuthResponseSchema;
export {
  ApprovedResponseSchema, BinInfoSchema, DeclinedResponseSchema,
  RetryResponseSchema, UserFieldSchema
};

